<script setup lang="ts">
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type PermissionHelpType = 'file' | 'term'

const props = defineProps<{
  kind: PermissionHelpType
  active: boolean
  pinned: boolean
}>()

const emit = defineEmits<{
  (event: 'open', kind: PermissionHelpType): void
  (event: 'keep', kind: PermissionHelpType): void
  (event: 'leave', kind: PermissionHelpType): void
  (event: 'toggle', kind: PermissionHelpType): void
}>()

const { locale } = useI18n()
const trigger = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const tooltipContent = ref<HTMLElement>()
const tooltipId = `enterprise-permission-tooltip-${useId().replaceAll(':', '')}`
const position = ref({
  top: 0,
  left: 0,
  arrowLeft: 24,
  maxHeight: 480,
  placement: 'below' as 'above' | 'below',
})

const rtl = computed(() => String(locale.value).toLowerCase().startsWith('ar'))
const contentKeys = computed(() => props.kind === 'file'
  ? {
      title: 'enterprise_permission_file_tooltip_title',
      firstLabel: 'account_enterprise_permission_look',
      firstDescription: 'enterprise_permission_file_view_desc',
      secondLabel: 'account_enterprise_permission_download',
      secondDescription: 'enterprise_permission_file_download_desc',
      warning: 'enterprise_permission_file_none_desc',
      own: 'enterprise_permission_file_own_desc',
    }
  : {
      title: 'enterprise_permission_term_tooltip_title',
      firstLabel: 'account_enterprise_permission_term_use',
      firstDescription: 'enterprise_permission_term_use_desc',
      secondLabel: 'account_enterprise_permission_edit',
      secondDescription: 'enterprise_permission_term_edit_desc',
      warning: 'enterprise_permission_term_none_desc',
      own: 'enterprise_permission_term_own_desc',
    })

async function updatePosition() {
  if (!props.active || !trigger.value) return
  await nextTick()
  if (!tooltip.value || !tooltipContent.value) return

  const rect = trigger.value.getBoundingClientRect()
  const width = tooltip.value.offsetWidth || 400
  const viewportPadding = 12
  const gap = 8
  const anchorCenter = rect.left + rect.width / 2
  const left = Math.min(
    Math.max(anchorCenter - width / 2, viewportPadding),
    Math.max(viewportPadding, window.innerWidth - viewportPadding - width),
  )
  const fullHeight = tooltipContent.value.scrollHeight
  const availableBelow = Math.max(0, window.innerHeight - rect.bottom - gap - viewportPadding - 2)
  const availableAbove = Math.max(0, rect.top - gap - viewportPadding - 2)
  const placement = availableBelow < Math.min(fullHeight, 260) && availableAbove > availableBelow ? 'above' : 'below'
  const availableHeight = placement === 'above' ? availableAbove : availableBelow
  const maxHeight = Math.max(1, Math.floor(availableHeight))
  const renderedHeight = Math.min(fullHeight, maxHeight)

  position.value = {
    top: placement === 'above'
      ? Math.max(viewportPadding, rect.top - gap - renderedHeight - 2)
      : rect.bottom + gap,
    left,
    arrowLeft: Math.min(Math.max(anchorCenter - left - 7, 18), width - 32),
    maxHeight,
    placement,
  }
}

function onViewportScroll(event: Event) {
  if (event.target instanceof Node && tooltip.value?.contains(event.target)) return
  void updatePosition()
}

watch(
  [() => props.active, () => locale.value],
  ([active]) => {
    if (active) void updatePosition()
  },
  { flush: 'post' },
)

onMounted(() => {
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', onViewportScroll, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onViewportScroll, true)
})
</script>

<template>
  <span
    class="permission-help"
    data-permission-help
    :class="{ active }"
    @mouseenter="emit('open', kind)"
    @mouseleave="emit('leave', kind)"
  >
    <button
      ref="trigger"
      class="permission-help__trigger"
      type="button"
      :aria-label="$t(contentKeys.title)"
      aria-haspopup="dialog"
      :aria-expanded="active"
      :aria-pressed="pinned"
      :aria-controls="active ? tooltipId : undefined"
      @focus="emit('keep', kind)"
      @blur="emit('leave', kind)"
      @click.stop="emit('toggle', kind)"
    >
      <WorkbenchIcon name="info" :size="12" />
    </button>

    <Teleport to="body">
      <section
        v-if="active"
        :id="tooltipId"
        ref="tooltip"
        class="permission-tooltip"
        data-permission-help
        :class="{ 'is-above': position.placement === 'above' }"
        :dir="rtl ? 'rtl' : 'ltr'"
        role="dialog"
        :aria-label="$t(contentKeys.title)"
        :style="{
          top: `${position.top}px`,
          left: `${position.left}px`,
          '--permission-arrow-left': `${position.arrowLeft}px`,
          '--permission-max-height': `${position.maxHeight}px`,
        }"
        @mouseenter="emit('keep', kind)"
        @mouseleave="emit('leave', kind)"
        @wheel.stop
        @touchmove.stop
      >
        <div ref="tooltipContent" class="permission-tooltip__content">
          <strong class="permission-tooltip__title">{{ $t(contentKeys.title) }}</strong>
          <span class="permission-tooltip__row">
            <b>{{ $t(contentKeys.firstLabel) }}</b>
            <span>{{ $t(contentKeys.firstDescription) }}</span>
          </span>
          <span class="permission-tooltip__row">
            <b>{{ $t(contentKeys.secondLabel) }}</b>
            <span>{{ $t(contentKeys.secondDescription) }}</span>
          </span>
          <span class="permission-tooltip__warning">{{ $t(contentKeys.warning) }}</span>
          <span class="permission-tooltip__own">{{ $t(contentKeys.own) }}</span>
        </div>
      </section>
    </Teleport>
  </span>
</template>

<style scoped>
.permission-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.permission-help__trigger {
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  color: #98a2b3;
  background: transparent;
  cursor: pointer;
}

.permission-help.active .permission-help__trigger,
.permission-help__trigger:hover,
.permission-help__trigger:focus-visible {
  color: #2b7de9;
  outline: none;
}

.permission-tooltip {
  position: fixed;
  z-index: 3300;
  width: 400px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  border: 1px solid #ebeef2;
  border-radius: 12px;
  color: #51565d;
  background: #fff;
  box-shadow: 0 10px 30px #0a234d2e;
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  white-space: normal;
}

.permission-tooltip__content {
  display: flex;
  max-height: var(--permission-max-height, calc(100vh - 24px));
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 11px;
  scrollbar-width: thin;
  scrollbar-color: #cbd2dc transparent;
}

.permission-tooltip__content::-webkit-scrollbar { width: 6px; }
.permission-tooltip__content::-webkit-scrollbar-thumb { border-radius: 6px; background: #cbd2dc; }

.permission-tooltip::before {
  position: absolute;
  top: -7px;
  left: var(--permission-arrow-left);
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: solid #ebeef2;
  border-width: 1px 0 0 1px;
  background: #fff;
  content: '';
  transform: rotate(45deg);
}

.permission-tooltip.is-above::before {
  top: auto;
  bottom: -7px;
  border-width: 0 1px 1px 0;
}

.permission-tooltip__title {
  color: #1f2329;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
}

.permission-tooltip__row {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.permission-tooltip__row b {
  display: inline-flex;
  min-height: 21px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  padding: 2px 7px;
  border-radius: 4px;
  color: #2b7de9;
  background: #eaf3fe;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 17px;
  white-space: nowrap;
}

.permission-tooltip__warning {
  padding: 9px 12px;
  border-radius: 8px;
  color: #92400e;
  background: #fff4e5;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.6;
}

.permission-tooltip__own {
  color: #51565d;
  font-size: 11.5px;
  line-height: 1.6;
}
</style>
